import Grid from "@mui/material/Grid2";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTheme } from "@mui/material/styles";
import {
    Card,
    Collapse,
    IconButton,
    Typography,
    Box,
    Tooltip,
    CircularProgress,
    Button,
} from "@mui/material";
import {
    ExpandMore,
    ExpandLess,
    Download,
} from "@mui/icons-material";
import { ColDef } from "ag-grid-community";
import { SkeletonLoader } from "../../modules/dashboard/shared";
import ReportsList from "./ReportsList";
import CreateNewReport, { CreateNewReportHandle } from "./CreateNewReport";
import AgGridTable from "../../components/table/AgGridTable";
import { toast } from "react-toastify";
import { END_POINTS } from "../../shared";
import axiosInstance from "../../axios/axios";
import { formatDate } from "../../utils/FormatDate";
import ConfirmationModal from "../../components/ConfirmationModal";
// import ConfirmationToast from "../../components/ConfirmationToast";
// import eventEmitter from "../../components/utils/eventEmitter";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import noDataReport from "../../assets/no_data_report.svg";
import { setDeletedReportId } from "../onPremises/myScan/MyScanTable_Details_slice";
import WhiteTooltip from "../../components/WhiteTooltip";
import { sortReportsByCreatedAtDesc } from "../../utils/sortReports";



const checkAnySelected = (list: any[]): boolean => {
    if (!list) return false;
    for (const item of list) {
        if (item.selected) return true;
        if (item.data && checkAnySelected(item.data)) return true;
    }
    return false;
};

const hasOriginSelected = (list: any[], origin: string): boolean => {
    if (!list) return false;
    for (const item of list) {
        if (item.origin === origin && item.selected) return true;
        if (item.data && hasOriginSelected(item.data, origin)) return true;
    }
    return false;
};

const Reports = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const dispatch = useDispatch();
    const [activeReport, setActiveReport] = useState<any>(null);
    const [creationType, setCreationType] = useState<string | null>(null);
    const [selectedReportType, setSelectedReportType] = useState<string | null>(null);
    const [isReportNamesExpanded, setIsReportNamesExpanded] = useState(true);
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const leftGridRef = useRef<HTMLDivElement>(null);
    const rightGridRef = useRef<HTMLDivElement>(null);
    const [leftGridLoading, setLeftGridLoading] = useState(false);
    const [rightGridLoading, setRightGridLoading] = useState(false);
    const createFormRef = useRef<CreateNewReportHandle | null>(null);
    const [apiReportsData, setApiReportsData] = useState<any[]>([]);
    const [allReportsData, setAllReportsData] = useState<any[]>([]);
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
    const [reportTypes, setReportTypes] = useState<any[]>([]);
    const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [triggerScheduleDrawer, setTriggerScheduleDrawer] = useState(false);
    const [scheduleData, setScheduleData] = useState<any>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isReportTypesLoading, setIsReportTypesLoading] = useState(false);

    const isNewUserTrue = useSelector(
        (state: RootState) => state.UserLoginDetails.isNewUserTrue
    );
    const isOnlyCloudUser = useSelector(
        (state: RootState) => state.UserLoginDetails.isOnlyCloudUser
    );
    const isOnlyPremAssetUser = useSelector(
        (state: RootState) => state.UserLoginDetails.isOnlyPremAssetUser
    );
    const isBothCloudAndPremAssetUser = useSelector(
        (state: RootState) => state.UserLoginDetails.isBothCloudAndPremAssetUser
    );
    const showNoReportsState =
        !isNewUserTrue &&
        (allReportsData?.length ?? 0) === 0;

    const shouldShowPlusIcon =
        !isNewUserTrue &&
        (isOnlyPremAssetUser ||
            isOnlyCloudUser ||
            isBothCloudAndPremAssetUser);


    const fetchReportTypes = useCallback(async () => {
        try {
            setIsReportTypesLoading(true);
            const [typesResponse, filterResponse] = await Promise.all([
                axiosInstance.get(END_POINTS.REPORT_TYPES),
                axiosInstance.get(END_POINTS.GLOBAL_DASHBOARD_REGION).catch(() => ({ data: { data: {} } }))
            ]);

            const filterData = filterResponse?.data?.data || {};

            const isCloudSelected = 
                filterData.all_cloud || 
                filterData.all_cloud_accounts || 
                filterData.all_cloud_resource_types || 
                filterData.all_cloud_regions ||
                checkAnySelected(filterData.cloud_accounts_list) ||
                checkAnySelected(filterData.cloud_resource_types_list) ||
                checkAnySelected(filterData.cloud_regions_list) ||
                hasOriginSelected(filterData.vendor_list, 'cloud');

            const isOnPremSelected = 
                filterData.all_regions ||
                filterData.all_categories ||
                filterData.all_models ||
                checkAnySelected(filterData.regions_list) ||
                checkAnySelected(filterData.category_list) ||
                checkAnySelected(filterData.model_list) ||
                hasOriginSelected(filterData.vendor_list, 'onprem');

            // Support both old array format and new { report_types: string[] }
            const rawTypes = Array.isArray(typesResponse.data)
                ? typesResponse.data
                : Array.isArray(typesResponse?.data?.report_types)
                    ? typesResponse.data.report_types
                    : [];

            // Normalize to objects expected by UI (name/type/value)
            const normalizedTypes = rawTypes.map((rt: any) => {
                if (typeof rt === "string") {
                    return { name: rt, type: rt, value: rt };
                }
                const name = rt.name || rt.type || rt.value || "";
                return { name, type: rt.type || name, value: rt.value || name };
            });

            let filteredReportTypes: any[] = normalizedTypes;

            // Filter report types based on user flags
            if (isOnlyPremAssetUser) {
                filteredReportTypes = normalizedTypes.filter((reportType: any) => {
                    const reportName = reportType.name || reportType.type || "";
                    return reportName.includes("Asset") || reportName.includes("Organization");
                });
            } else if (isOnlyCloudUser) {
                filteredReportTypes = normalizedTypes.filter((reportType: any) => {
                    const reportName = reportType.name || reportType.type || "";
                    return reportName.includes("Resource") || reportName.includes("Organization");
                });
            }

            if (Object.keys(filterData).length > 0) {
                if (!isCloudSelected) {
                    filteredReportTypes = filteredReportTypes.filter((reportType: any) => {
                        const reportName = reportType.name || reportType.type || "";
                        return !reportName.includes("Resource");
                    });
                }
                
                if (!isOnPremSelected) {
                    filteredReportTypes = filteredReportTypes.filter((reportType: any) => {
                        const reportName = reportType.name || reportType.type || "";
                        return !reportName.includes("Asset");
                    });
                }
            }

            setReportTypes(filteredReportTypes);
        } catch (error) {
            toast.error("Failed to fetch report types");
        } finally {
            setIsReportTypesLoading(false);
        }
    }, [isOnlyPremAssetUser, isOnlyCloudUser]);

    const fetchReportsData = async (reportId?: string) => {
        try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (!reportId) {
                // When fetching all reports (no reportId), show both loaders
                setLeftGridLoading(true);
                setRightGridLoading(true);
            } else {
                // When fetching specific report data, only show right loader
                setRightGridLoading(true);
            }

            const response = await axiosInstance.get(
                `${END_POINTS.REPORT_OVERALL_HISTORY}?timezone=${timeZone}${reportId ? `&report_id=${reportId}` : ''}`
            );

            if (reportId) {
                // When fetching specific report data, update apiReportsData
                setApiReportsData(sortReportsByCreatedAtDesc(response.data));
            } else {
                // When fetching all reports, update both states
                setAllReportsData(sortReportsByCreatedAtDesc(response.data));
            }
        } catch (error) {
            // toast.error("Failed to fetch reports data");
            console.log(error);
        } finally {
            // Reset loaders based on the scenario
            if (!reportId) {
                // When fetching all reports, hide both loaders
                setLeftGridLoading(false);
                setRightGridLoading(false);
            } else {
                // When fetching specific report, only hide right loader
                setRightGridLoading(false);
            }
        }
    };

    const handleReportDownload = async (report: any) => {
        setDownloadingReportId(report.id);
        try {
            // Block download if report is not completed
            const statusText = report?.status_display?.toLowerCase?.() || "";
            if (statusText !== "completed") {
                toast.error("Report is not ready to download yet");
                return;
            }

            const recordId = report?.id;
            if (!recordId) {
                toast.error("Report ID not found");
                return;
            }

            const isHistoryRecord = Boolean(report?.report_request_id);
            const historyId = report?.id;

            const paramsPayload = isHistoryRecord
                ? { history_id: historyId }
                : { report_id: recordId };

            const response = await axiosInstance.get(
                END_POINTS.DOWNLOAD_LINK_ENDPOINT,
                {
                    params: paramsPayload,
                }
            );
            // Generate filename based on report name and current date
            const now = new Date();
            const dateStr = `${String(now.getMonth() + 1).padStart(2, "0")}${String(
                now.getDate()
            ).padStart(2, "0")}${now.getFullYear()}`;
            const fileName = `${report.custom_report_name ? report.custom_report_name.replace(
                /\s+/g,
                "_"
            ) : report.report_type.replace(
                /\s+/g,
                "_"
            )}-${dateStr}.pdf`;

            // Create download link using the URL from API response
            const link = document.createElement("a");
            link.href = response?.data?.download_url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);

            toast.success("Report downloaded successfully !");

            // Refresh tables: latest reports and, if applicable, the active report's history
            await fetchReportsData();
            if (activeReport?.id) {
                await fetchReportsData(activeReport.id);
            }
        } catch (error: any) {
            toast.error("Failed to download report, please try again.");
        } finally {
            setDownloadingReportId(null);
        }
    };

    // Dynamically create columnDefs
    const generateColumnDefs = (): ColDef[] => [
        {
            field: "custom_report_name",
            headerName: "Name",
            flex: 1,
            minWidth: 180,
            filter: false,
            lockPosition: true,
            lockPinned: true,
            cellRenderer: (params: any) => (
                <Tooltip title={params.value} arrow>
                    <span className="ag-ellipsis">{params.value || '-'}</span>
                </Tooltip>
            ),
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1.5,
            filter: false,
            minWidth: 160,
            cellRenderer: (params: any) => (
                <Tooltip title={params.value} arrow>
                    <span className="ag-ellipsis">{params.value || '-'}</span>
                </Tooltip>
            ),
        },
        {
            field: "completed_at",
            headerName: "Last Generated Date",
            minWidth: 180,
            filter: false,
            cellRenderer: (params: any) => (
                <Tooltip title={formatDate(params.value)} arrow>
                    <span className="ag-ellipsis">
                        {params.value ? formatDate(params.value) : '-'}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "report_format",
            headerName: "Format",
            minWidth: 100,
            filter: false,
            cellRenderer: (params: any) => (
                <Tooltip title={params.value} arrow>
                    <span className="ag-ellipsis">{(params.value).toUpperCase() || '-'}</span>
                </Tooltip>
            ),
        },
        {
            field: "created_at",
            headerName: "Date",
            minWidth: 180,
            filter: false,
            cellRenderer: (params: any) => (
                <Tooltip title={formatDate(params.value)} arrow>
                    <span className="ag-ellipsis">
                        {params.value ? formatDate(params.value) : '-'}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "modified_by_name",
            headerName: "Last Modified By",
            minWidth: 160,
            filter: false,
            cellRenderer: (params: any) => (
                <Tooltip title={params.value} arrow>
                    <span className="ag-ellipsis">
                        {params.value || '-'}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: "modified_at",
            headerName: "Last Modified Date",
            // minWidth: 160,
            filter: false,
            cellRenderer: (params: any) => (
                <Tooltip title={formatDate(params.value)} arrow>
                    <span className="ag-ellipsis">
                        {params.value ? formatDate(params.value) : '-'}
                    </span>
                </Tooltip>
            ),
        },
        {
            filter: false,
            pinned: 'right',
            lockPosition: true,
            lockPinned: true,
            suppressColumnsToolPanel: true,
            width: 50,
            cellRenderer: (params: any) => {
                const isDownloading = downloadingReportId === params.data.id;
                const isCompleted = params.data.status_display && params.data.status_display.toLowerCase() === "completed";
                const createdAt = params?.data?.created_at ? new Date(params.data.created_at) : null;
                const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
                const isExpired = createdAt ? (Date.now() - createdAt.getTime()) >= sevenDaysInMs : false;
                const canDownload = isCompleted && !isDownloading && !isExpired;

                return (
                    <span
                        style={{
                            opacity: isCompleted && !isExpired ? 1 : 0.4,
                            cursor: canDownload ? "pointer" : "not-allowed",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {isDownloading ? (
                            <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={10} sx={{ color: 'white' }} />
                                <Typography sx={{ color: 'white', fontSize: '11px' }}>
                                    Downloading...
                                </Typography>
                            </Box>
                        ) : (
                            <WhiteTooltip title={isExpired ? "Expired" : ""} arrow disableHoverListener={!isExpired}>
                                <span>
                                    <Download
                                        onClick={canDownload ? () => handleReportDownload(params.data) : undefined}
                                        sx={{
                                            color: canDownload ? "black" : "gray",
                                            marginTop: "10px",
                                        }}
                                    />
                                </span>
                            </WhiteTooltip>
                        )}
                    </span>
                );
            },
        }
    ];

    // Fetch initial data
    useEffect(() => {
        fetchReportsData(); // Load all reports by default
        fetchReportTypes();
    }, [fetchReportTypes]);

    useEffect(() => {
        if (apiReportsData.length > 0 || allReportsData.length > 0) {
            setColumnDefs(generateColumnDefs());
        }
    }, [apiReportsData, allReportsData]);

    // Listen for report data updates from socket
    // useEffect(() => {
    //     const handleReportDataUpdate = () => {
    //         ConfirmationToast({
    //             message: "Reports data updated, we are refreshing the page",
    //             onConfirm: () => {
    //                 fetchReportsData();
    //             },
    //             onCancel: () => {
    //                 // Do nothing when user cancels
    //             },
    //             confirmText: "Yes",
    //             cancelText: "No",
    //             autoCloseTime: 10000, // 10 seconds auto close
    //         });
    //     };
    //     eventEmitter.on('filterSaved', handleReportDataUpdate);
    //     return () => {
    //         eventEmitter.off('filterSaved', handleReportDataUpdate);
    //     };
    // }, []);

    const handleActiveReportChange = (report: any) => {
        setActiveReport(report);
        setCreationType(null);

        // Set the selectedReportType based on the report data
        if (report) {
            // Find the report type from reportTypes array that matches the report
            const matchingReportType = reportTypes.find((rt: any) =>
                rt.name === report.report || rt.type === report.report
            );
            setSelectedReportType(matchingReportType || report.report);
        } else {
            setSelectedReportType(null);
        }

        // Fetch data for the selected report only
        if (report && report.id) {
            fetchReportsData(report.id);
        }
    };

    const handleCreateNewReport = (reportType?: any) => {
        setCreationType("new");
        setSelectedReportType(reportType);
        setActiveReport(null);
        // Clear any existing report selection in the list
        setIsEditMode(false);
    };

    const handleToggleReportNames = () => {
        setIsReportNamesExpanded(!isReportNamesExpanded);
    };

    // Handle schedule update from CreateNewReport component
    const handleScheduleUpdate = async (newScheduleData: any) => {
        if (createFormRef.current && activeReport?.id) {
            setIsUpdating(true);
            try {
                // Use the schedule data from the drawer
                const is_scheduled = newScheduleData.is_scheduled;
                const is_download = newScheduleData.is_download;
                // Get the update payload from CreateNewReport component with schedule data
                const updatePayload = createFormRef.current.buildUpdatePayload(is_scheduled, is_download, newScheduleData);
                // Make PUT request to update the report
                const response = await axiosInstance.put(
                    `${END_POINTS.REPORT_UPDATE_DELETE}/${activeReport.id}`,
                    updatePayload
                );
                console.log("Response data:", response.data);
                toast.success("Report scheduled successfully !");

                const currentReportId = activeReport.id;

                // Fetch all reports to update the left sidebar
                await fetchReportsData();

                // Fetch the specific report's history to update the history table
                await fetchReportsData(currentReportId);

                // Fetch fresh data for the active report to update the form
                const refreshResponse = await axiosInstance.get(`${END_POINTS.REPORT_OVERALL_HISTORY}`);
                if (refreshResponse.data && Array.isArray(refreshResponse.data)) {
                    const updatedReport = refreshResponse.data.find((report: any) => report.id === currentReportId);
                    if (updatedReport) {
                        setActiveReport(updatedReport);
                    }
                }
            } catch (error: any) {
                toast.error(
                    error.response?.data?.message || "Failed to schedule report",
                    { type: "error" }
                );
            } finally {
                setIsUpdating(false);
            }
        }
    };

    // Handle update report functionality
    const handleUpdate = async () => {
        if (createFormRef.current && activeReport?.id) {
            setIsUpdating(true);
            try {
                // Determine is_scheduled and is_download based on the active report or schedule data
                const is_scheduled = scheduleData?.is_scheduled ?? (activeReport.is_scheduled ? true : false);
                const is_download = scheduleData?.is_download ?? (activeReport.is_download ? true : false);

                // Get the update payload from CreateNewReport component
                const updatePayload = createFormRef.current.buildUpdatePayload(is_scheduled, is_download, scheduleData);

                console.log("Update payload:", updatePayload);

                // Make PUT request to update the report
                const response = await axiosInstance.put(
                    `${END_POINTS.REPORT_UPDATE_DELETE}/${activeReport.id}`,
                    updatePayload
                );

                console.log("Update response:", response);
                toast.success("Report updated successfully !");

                const currentReportId = activeReport.id;

                // Fetch all reports to update the left sidebar
                await fetchReportsData();

                // Fetch the specific report's history to update the history table
                await fetchReportsData(currentReportId);

                // Fetch fresh data for the active report to update the form
                const refreshResponse = await axiosInstance.get(`${END_POINTS.REPORT_OVERALL_HISTORY}`);
                if (refreshResponse.data && Array.isArray(refreshResponse.data)) {
                    const updatedReport = refreshResponse.data.find((report: any) => report.id === currentReportId);
                    if (updatedReport) {
                        setActiveReport(updatedReport);
                    }
                }

                // Clear schedule data after successful update
                setScheduleData(null);
            } catch (error: any) {
                toast.error(
                    error.response?.data?.message || "Failed to update report",
                    { type: "error" }
                );
            } finally {
                setIsUpdating(false);
                setIsEditMode(false);
            }
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        try {
            const reportId = activeReport?.id;
            if (!reportId) {
                toast.error("Report ID not found");
                setIsDeleting(false);
                return;
            }

            // Find the current report index in allReportsData
            const currentIndex = allReportsData.findIndex(report => report.id === reportId);
            const nextReport = currentIndex >= 0 && currentIndex < allReportsData.length - 1
                ? allReportsData[currentIndex + 1]
                : currentIndex > 0
                    ? allReportsData[currentIndex - 1]
                    : allReportsData.length > 1
                        ? allReportsData[1]
                        : null;

            const response = await axiosInstance.delete(`${END_POINTS.REPORT_UPDATE_DELETE}/${reportId}`);
            if (response.status === 204 || response.status === 200) {
                toast.success(response?.data?.message || "Report deleted successfully !");
                setShowDeleteModal(false);
                dispatch(setDeletedReportId(reportId));
            }

            // Fetch updated data
            await fetchReportsData();

            // Select the next report if available
            if (nextReport) {
                setActiveReport(nextReport);
                fetchReportsData(nextReport.id);
            } else {
                setActiveReport(null);
            }

            setIsEditMode(false);
        } catch (error: any) {
            toast.error(
                error.response?.data?.message || "Failed to delete report",
                { type: "error" }
            );
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
    };

    const handleScheduleDrawerTriggered = () => {
        setTriggerScheduleDrawer(false);
    };

    return (
        <>
            {leftGridLoading ? (
                <div
                    style={{
                        height: "calc(100vh - 104px)",
                        width: "100%",
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <Grid container spacing={2} sx={{ height: "100%" }}>
                        <Grid size={2.5} sx={{ height: "100%" }} ref={leftGridRef}>
                            <SkeletonLoader
                                count={Math.floor(window.innerHeight / 40)}
                                styles={{ height: 30 }}
                            />
                        </Grid>
                        <Grid size={9.5} sx={{ height: "100%" }} ref={rightGridRef}>
                            <SkeletonLoader
                                count={Math.floor(window.innerHeight / 40)}
                                styles={{ height: 30 }}
                            />
                        </Grid>
                    </Grid>
                </div>
            ) : (
                <div>
                    <Grid container spacing={2} sx={{ height: "calc(100vh - 104px)" }}>
                        {/* Left Sidebar */}
                        <Grid size={2.5} sx={{ height: "100%" }} ref={leftGridRef}>
                            <ReportsList
                                onActiveReportChange={handleActiveReportChange}
                                onCreateNewReport={handleCreateNewReport}
                                isDarkMode={isDarkMode}
                                reportsData={isNewUserTrue ? [] : allReportsData}
                                reportTypes={reportTypes}
                                onEditModeChange={setIsEditMode}
                                activeReport={activeReport}
                                showAddIcon={shouldShowPlusIcon}
                                onAddClick={fetchReportTypes}
                                isReportTypesLoading={isReportTypesLoading}
                            />
                        </Grid>

                        {/* Right Side */}

                        <Grid
                            size={9.5}
                            sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
                            ref={rightGridRef}
                        >
                            {rightGridLoading ? (
                                <SkeletonLoader
                                    count={Math.floor(window.innerHeight / 40)}
                                    styles={{ height: 30 }}
                                />
                            ) : (
                                <>
                                    {/* Create Report View */}
                                    {creationType === "new" ? (
                                        <Card
                                            sx={{
                                                backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                                                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                                flex: "1 1 auto",
                                                display: "flex",
                                                flexDirection: "column",
                                                overflow: "hidden",
                                                height: "calc(100vh - 102px)",
                                            }}
                                            className="white-box"
                                        >
                                            <Box sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                                <CreateNewReport
                                                    selectedReportType={selectedReportType}
                                                    hideButtons={false}
                                                    onCancel={() => {
                                                        setCreationType(null);
                                                        setSelectedReportType(null);
                                                    }}
                                                    reportData={activeReport}
                                                    onRefreshReports={() => fetchReportsData()}
                                                    triggerScheduleDrawer={triggerScheduleDrawer}
                                                    onScheduleDrawerTriggered={handleScheduleDrawerTriggered}
                                                    onScheduleUpdate={handleScheduleUpdate}
                                                />
                                            </Box>
                                        </Card>
                                    ) : activeReport && (
                                        <Card
                                            sx={{
                                                backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                                                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                                flex: "0 0 auto",
                                                maxHeight: isReportNamesExpanded ? "305px" : "60px",
                                            }}
                                            className="white-box"
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "16px 24px",
                                                    borderBottom: isReportNamesExpanded ? "1px solid #E2E2F2" : "none",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={handleToggleReportNames}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontSize: "18px",
                                                            fontWeight: 600,
                                                            color: isDarkMode ? "#ffffff" : "#000000",
                                                        }}
                                                    >
                                                        {activeReport.report}
                                                    </Typography>
                                                    <IconButton size="small">
                                                        {isReportNamesExpanded ? <ExpandLess /> : <ExpandMore />}
                                                    </IconButton>
                                                </Box>

                                                {/* Buttons beside expand/collapse */}
                                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                    {!isEditMode && (
                                                        <>
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => setIsEditMode(true)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={handleDeleteClick}
                                                                startIcon={isDeleting && <CircularProgress size={16} sx={{ color: 'blue' }} />}
                                                            >
                                                                {isDeleting ? "Deleting..." : "Delete"}
                                                            </Button>
                                                        </>
                                                    )}
                                                    {isEditMode && (
                                                        <>
                                                            <Button
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => {
                                                                    setIsEditMode(false);
                                                                }}
                                                                disabled={isUpdating}
                                                            >
                                                                Cancel
                                                            </Button>
                                                            <Button
                                                                onClick={() => {
                                                                    setTriggerScheduleDrawer(true);
                                                                }}
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                disabled={isUpdating}
                                                            >
                                                                Schedule Report
                                                            </Button>
                                                            <Button
                                                                onClick={handleUpdate}
                                                                variant="outlined"
                                                                color="primary"
                                                                size="small"
                                                                disabled={isUpdating}
                                                                startIcon={isUpdating && <CircularProgress size={16} sx={{ color: 'blue' }} />}
                                                            >
                                                                {isUpdating ? "Updating..." : "Update"}
                                                            </Button>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>

                                            <Collapse in={isReportNamesExpanded}>
                                                <Box className="scroll" sx={{ padding: "12px 24px", maxHeight: "270px", overflow: "auto" }}>
                                                    <CreateNewReport
                                                        ref={createFormRef}
                                                        selectedReportType={selectedReportType}
                                                        hideButtons={true}
                                                        onCancel={() => {
                                                            setCreationType(null);
                                                            setSelectedReportType(null);
                                                            setActiveReport(null);
                                                            setIsEditMode(false);
                                                        }}
                                                        reportData={apiReportsData && apiReportsData.length > 0 ? apiReportsData[0] : activeReport}
                                                        isEditable={isEditMode}
                                                        isEditMode={isEditMode}
                                                        onEditModeChange={setIsEditMode}
                                                        onRefreshReports={() => fetchReportsData()}
                                                        triggerScheduleDrawer={triggerScheduleDrawer}
                                                        onScheduleDrawerTriggered={handleScheduleDrawerTriggered}
                                                        onScheduleUpdate={handleScheduleUpdate}
                                                    />
                                                </Box>
                                            </Collapse>
                                        </Card>
                                    )}

                                    {/* History */}
                                    {creationType !== "new" && (
                                        <Card
                                            sx={{
                                                backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                                                boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                                                flex: "1 1 auto",
                                                display: "flex",
                                                flexDirection: "column",
                                                minHeight: 0,
                                            }}
                                            className="white-box"
                                        >
                                            <>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        padding: "16px 24px",
                                                        borderBottom: "1px solid #E2E2F2",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {activeReport ? (
                                                        <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    fontSize: "18px",
                                                                    fontWeight: 600,
                                                                    color: isDarkMode ? "#ffffff" : "#000000",
                                                                }}
                                                            >
                                                                History
                                                            </Typography>
                                                            <IconButton size="small">
                                                                {isHistoryExpanded ? <ExpandLess /> : <ExpandMore />}
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontSize: "18px",
                                                                fontWeight: 600,
                                                                color: isDarkMode ? "#ffffff" : "#000000",
                                                            }}
                                                        >
                                                            Latest Reports
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {activeReport ? (
                                                    <Collapse in={isHistoryExpanded}>
                                                        <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
                                                            <AgGridTable
                                                                columnDefs={columnDefs}
                                                                data={isNewUserTrue ? [] : apiReportsData}
                                                                height={"calc(30vh - 20px)"}
                                                                showSearch
                                                                searchPlaceholder="Search Report..."
                                                                pagination={true}
                                                            />
                                                        </Box>
                                                    </Collapse>
                                                ) : (
                                                    <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
                                                        {isNewUserTrue || showNoReportsState ? (
                                                            <Box
                                                                sx={{
                                                                    height: "calc(100vh - 210px)",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    flexDirection: "column",
                                                                    gap: 2,
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={noDataReport}
                                                                    alt="No reports"
                                                                    sx={{ width: 220, opacity: 0.8 }}
                                                                />
                                                                <Typography sx={{ color: isDarkMode ? "#cccccc" : "#666666" }}>
                                                                    {isNewUserTrue
                                                                        ? "No devices are there"
                                                                        : "No reports yet! Create your first report by clicking the + icon above.."}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <AgGridTable
                                                                columnDefs={columnDefs}
                                                                data={isNewUserTrue ? [] : allReportsData}
                                                                height={"calc(100vh - 210px)"}
                                                                showSearch
                                                                searchPlaceholder="Search Report..."
                                                                pagination={true}
                                                            />
                                                        )}
                                                    </Box>
                                                )}
                                            </>
                                        </Card>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>
                </div>
            )}

            <ConfirmationModal
                open={showDeleteModal}
                onClose={handleCloseDeleteModal}
                onConfirm={handleConfirmDelete}
                title="Are you sure want to delete this report?"
                message="You can not recover this file once it gets deleted."
                confirmButtonText="Delete"
                confirmButtonColor="error"
                isLoading={isDeleting}
            />
        </>
    );
};

export default Reports;
